import { useEffect, useMemo, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import ReconNotesPanel from "@/components/ReconNotesPanel";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/contexts/AuthContext";

export default function TeamNotes() {
  const { user } = useAuth();
  const [search, setSearch] = useState("");
  const [selectedClientName, setSelectedClientName] = useState<string | null>(null);

  const { data: clients = [], isLoading } = trpc.clientStatus.listAll.useQuery(undefined, {
    refetchInterval: 5_000,
  });

  const filteredClients = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    if (!normalizedSearch) return clients;
    return clients.filter((client) => client.clientName.toLowerCase().includes(normalizedSearch));
  }, [clients, search]);

  useEffect(() => {
    if (selectedClientName && clients.some((client) => client.clientName === selectedClientName)) return;
    setSelectedClientName(clients[0]?.clientName ?? null);
  }, [clients, selectedClientName]);

  const selectedClient = clients.find((client) => client.clientName === selectedClientName) ?? null;

  return (
    <DashboardLayout>
      <div className="mx-auto w-full max-w-6xl space-y-5 animate-fade-slide-up">
        <header className="rounded-2xl border border-[oklch(0.72_0.14_75_/_28%)] bg-[oklch(0.98_0.01_85)] px-5 py-5 shadow-sm">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-[oklch(0.45_0.12_75)]">Shared client collaboration</p>
              <h1 className="mt-1 text-2xl font-bold text-[oklch(0.14_0.04_240)]">Team Notes</h1>
              <p className="mt-1 max-w-2xl text-sm text-[oklch(0.38_0.03_240)]">
                View the current client work note and Recon.Notes recorded by every APX teammate. Updates refresh automatically while this page is open.
              </p>
            </div>
            <div className="rounded-lg border border-[oklch(0.72_0.14_75_/_35%)] bg-[oklch(0.95_0.02_75)] px-3 py-2 text-xs text-[oklch(0.32_0.08_75)]">
              Signed in as <span className="font-semibold">{user?.name ?? "APX Staff"}</span>
            </div>
          </div>
        </header>

        <div className="grid min-h-[540px] gap-4 lg:grid-cols-[minmax(260px,0.78fr)_minmax(0,1.7fr)]">
          <section className="overflow-hidden rounded-2xl border border-[oklch(0.86_0.01_240)] bg-white shadow-sm">
            <div className="border-b border-[oklch(0.9_0.01_240)] px-4 py-3">
              <label htmlFor="team-notes-client-search" className="text-sm font-semibold text-[oklch(0.18_0.04_240)]">Clients</label>
              <input
                id="team-notes-client-search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search a client…"
                className="mt-2 w-full rounded-lg border border-[oklch(0.84_0.02_240)] bg-[oklch(0.99_0.002_85)] px-3 py-2 text-sm text-[oklch(0.16_0.04_240)] outline-none transition focus:border-[oklch(0.55_0.14_75)]"
              />
            </div>
            <div className="max-h-[475px] overflow-y-auto p-2">
              {isLoading ? (
                <p className="px-3 py-6 text-sm text-[oklch(0.46_0.03_240)]">Loading clients…</p>
              ) : filteredClients.length === 0 ? (
                <p className="px-3 py-6 text-sm text-[oklch(0.46_0.03_240)]">No clients match the search.</p>
              ) : (
                filteredClients.map((client) => {
                  const selected = client.clientName === selectedClientName;
                  return (
                    <button
                      key={client.id}
                      type="button"
                      onClick={() => setSelectedClientName(client.clientName)}
                      className="mb-1 w-full rounded-lg px-3 py-2.5 text-left transition-colors"
                      style={{
                        background: selected ? "oklch(0.92 0.025 75)" : "transparent",
                        border: selected ? "1px solid oklch(0.72 0.14 75 / 50%)" : "1px solid transparent",
                      }}
                    >
                      <p className="truncate text-sm font-semibold text-[oklch(0.18_0.04_240)]">{client.clientName}</p>
                      <p className="mt-0.5 truncate text-xs text-[oklch(0.45_0.03_240)]">
                        {client.notes ? "Client work note available" : "No client work note"}
                      </p>
                    </button>
                  );
                })
              )}
            </div>
          </section>

          <section className="overflow-hidden rounded-2xl border border-[oklch(0.86_0.01_240)] bg-white shadow-sm">
            {!selectedClient ? (
              <div className="flex min-h-[440px] items-center justify-center px-6 text-center text-sm text-[oklch(0.46_0.03_240)]">
                Select a client to review their shared notes.
              </div>
            ) : (
              <div className="space-y-4 p-4">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.15em] text-[oklch(0.45_0.12_75)]">Client</p>
                  <h2 className="mt-1 text-xl font-bold text-[oklch(0.14_0.04_240)]">{selectedClient.clientName}</h2>
                </div>

                <div className="rounded-xl border border-[oklch(0.72_0.14_75_/_35%)] bg-[oklch(0.98_0.012_85)] p-4">
                  <div className="flex items-center gap-2">
                    <svg className="h-4 w-4 text-[oklch(0.45_0.12_75)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    <h3 className="text-sm font-bold text-[oklch(0.22_0.04_75)]">Client Work Note</h3>
                  </div>
                  <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-[oklch(0.2_0.04_240)]">
                    {selectedClient.notes || "No client work note has been saved yet."}
                  </p>
                </div>

                <ReconNotesPanel
                  clientId={selectedClient.clientName}
                  clientName={selectedClient.clientName}
                  isOpen={true}
                  onClose={() => setSelectedClientName(null)}
                />
              </div>
            )}
          </section>
        </div>
      </div>
    </DashboardLayout>
  );
}
